import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { CartProvider } from "@/contexts/CartContext";
import { WishlistProvider } from "@/contexts/WishlistContext";
import CartDrawer from "@/components/CartDrawer";
import ScrollToTop from "@/components/ScrollToTop";
import BackToTop from "@/components/BackToTop";
import EmailSignupPopup from "@/components/EmailSignupPopup";
import FloatingChat from "@/components/FloatingChat";
import Index from "./pages/Index.tsx";
import Shop from "./pages/Shop.tsx";
import ProductDetail from "./pages/ProductDetail.tsx";
import Checkout from "./pages/Checkout.tsx";
import OrderConfirmation from "./pages/OrderConfirmation.tsx";
import AdminLayout from "./pages/admin/AdminLayout.tsx";
import AdminLogin from "./pages/admin/AdminLogin.tsx";
import AdminProducts from "./pages/admin/AdminProducts.tsx";
import AdminEditProduct from "./pages/admin/AdminEditProduct.tsx";
import AdminImport from "./pages/admin/AdminImport.tsx";
import AdminOrders from "./pages/admin/AdminOrders.tsx";
import AdminSubscribers from "./pages/admin/AdminSubscribers.tsx";
import AdminDashboard from "./pages/admin/AdminDashboard.tsx";
import AdminOrderDetail from "./pages/admin/AdminOrderDetail.tsx";
import AdminCustomers from "./pages/admin/AdminCustomers.tsx";
import AdminDiscountCodes from "./pages/admin/AdminDiscountCodes.tsx";
import AdminActivityLog from "./pages/admin/AdminActivityLog.tsx";
import AdminSettings from "./pages/admin/AdminSettings.tsx";
import AdminReviews from "./pages/admin/AdminReviews.tsx";
import AdminShipping from "./pages/admin/AdminShipping.tsx";
import AdminRoyalMail from "./pages/admin/AdminRoyalMail.tsx";
import AdminBlog from "./pages/admin/AdminBlog.tsx";
import AdminImageMigration from "./pages/admin/AdminImageMigration.tsx";
import AdminNotifications from "./pages/admin/AdminNotifications.tsx";
import AdminSEOKeywords from "./pages/admin/AdminSEOKeywords.tsx";
import AdminAdBanners from "./pages/admin/AdminAdBanners.tsx";
import AdminAffiliateProducts from "./pages/admin/AdminAffiliateProducts.tsx";
import AdminBlogAutomation from "./pages/admin/AdminBlogAutomation.tsx";
import AuthPage from "./pages/AuthPage.tsx";
import AccountPage from "./pages/AccountPage.tsx";
import WishlistPage from "./pages/WishlistPage.tsx";
import ResetPassword from "./pages/ResetPassword.tsx";
import AboutPage from "./pages/AboutPage.tsx";
import TermsPage from "./pages/TermsPage.tsx";
import PrivacyPage from "./pages/PrivacyPage.tsx";
import DeliveryPage from "./pages/DeliveryPage.tsx";
import BlogPage from "./pages/BlogPage.tsx";
import BlogPostPage from "./pages/BlogPostPage.tsx";
import NotFound from "./pages/NotFound.tsx";
import SocialRedirect from "./pages/SocialRedirect.tsx";
import NHSWigSupplierPage from "./pages/NHSWigSupplierPage.tsx";
import SalonPage from "./pages/SalonPage.tsx";
import OAuthConsent from "./pages/OAuthConsent.tsx";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <BrowserRouter>
          <CartProvider>
            <WishlistProvider>
              <Toaster />
              <Sonner />
              <ScrollToTop />
              <BackToTop />
              <CartDrawer />
              <EmailSignupPopup />
              <FloatingChat />
              <div id="app-main-content">
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/shop" element={<Shop />} />
                  <Route path="/product/:slug" element={<ProductDetail />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/order-confirmation/:orderId" element={<OrderConfirmation />} />
                  <Route path="/auth" element={<AuthPage />} />
                  <Route path="/account" element={<AccountPage />} />
                  <Route path="/wishlist" element={<WishlistPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/delivery" element={<DeliveryPage />} />
                  <Route path="/blog" element={<BlogPage />} />
                  <Route path="/blog/:slug" element={<BlogPostPage />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/nhs-wig-supplier" element={<NHSWigSupplierPage />} />
                  <Route path="/salon" element={<SalonPage />} />
                  <Route path="/social/:platform" element={<SocialRedirect />} />
                  <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<AdminDashboard />} />
                    <Route path="products" element={<AdminProducts />} />
                    <Route path="edit/:id" element={<AdminEditProduct />} />
                    <Route path="import" element={<AdminImport />} />
                    <Route path="orders" element={<AdminOrders />} />
                    <Route path="orders/:id" element={<AdminOrderDetail />} />
                    <Route path="customers" element={<AdminCustomers />} />
                    <Route path="discounts" element={<AdminDiscountCodes />} />
                    <Route path="subscribers" element={<AdminSubscribers />} />
                    <Route path="notifications" element={<AdminNotifications />} />
                    <Route path="reviews" element={<AdminReviews />} />
                    <Route path="shipping" element={<AdminShipping />} />
                    <Route path="royal-mail" element={<AdminRoyalMail />} />
                    <Route path="blog" element={<AdminBlog />} />
                    <Route path="blog-automation" element={<AdminBlogAutomation />} />
                    <Route path="images" element={<AdminImageMigration />} />
                    <Route path="seo-keywords" element={<AdminSEOKeywords />} />
                    <Route path="ad-banners" element={<AdminAdBanners />} />
                    <Route path="affiliates" element={<AdminAffiliateProducts />} />
                    <Route path="activity" element={<AdminActivityLog />} />
                    <Route path="settings" element={<AdminSettings />} />
                  </Route>
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </div>
            </WishlistProvider>
          </CartProvider>
        </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
